import { Command } from '../types';
import { personalInfo, skillCategories, projects, workExperience, education, certifications, researchPublications } from '../data/portfolio';

export const commands: Command[] = [
  {
    name: 'test',
    aliases: ['t'],
    description: 'Test command to verify terminal functionality',
    handler: async () => ({
      output: `
=== TEST COMMAND ===
✅ Terminal is working correctly!
✅ Command execution is functional
✅ You can now use all portfolio commands

Try typing 'help' to see all available commands.
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'help',
    aliases: ['h', '?'],
    description: 'Display all available commands',
    handler: async () => ({
      output: `
Available Commands:
==================

Essential Commands:
  help          - Display all available commands
  about         - Personal and professional summary
  skills        - Technical skills categorized by domain
  projects      - Portfolio showcase with interactive media
  experience    - Work history and achievements
  education     - Academic background and certifications
  contact       - Professional contact information
  gravatar      - Interactive QR code and profile link
  timeline      - Career progression visualization
  achievements  - Notable accomplishments and publications
  clear         - Clear terminal screen
  theme         - Change color themes (green/amber/blue)
  test          - Test command to verify terminal functionality

Advanced Commands:
  secret        - Hidden Easter egg content
  status        - System information and easter eggs
  history       - Command history navigation
  whoami        - Extended personal bio with fun facts
  skills --tech - Filter technical skills only
  projects --type - Filter projects by category
  view          - View project details and media
  schedule      - Schedule a meeting through gravatar
  message       - Contact me on Telegram

Type 'help <command>' for detailed usage information.
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'about',
    aliases: ['a', 'info'],
    description: 'Personal and professional summary',
    handler: async () => ({
      output: `
=== MOHAMMAD TABISH MUKADDAM ===

🎯 Professional Title: ${personalInfo.title}
📍 Location: ${personalInfo.location}
📅 Date of Birth: ${personalInfo.dob}

🚀 Professional Summary:
I am a passionate Operations and Technology Assistant with expertise 
in game development, AI automation, and technical operations. My 
background spans from creating immersive gaming experiences in Unreal 
Engine 5 to implementing automation solutions that drive operational 
efficiency.

🎮 Core Expertise:
• Game Development (Unreal Engine 5)
• AI & Machine Learning Automation
• Operations & Technology Management
• Full-Stack Development
• Cloud Infrastructure & Databases

💡 What I Do:
I bridge the gap between technology and operations, creating solutions 
that enhance productivity and user experience. From developing tactical 
shooters to automating complex workflows, I bring technical innovation 
to every project.

Type 'skills' to explore my technical capabilities.
Type 'projects' to see my portfolio showcase.
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'skills',
    aliases: ['s', 'tech'],
    description: 'Technical skills categorized by domain',
    handler: async (args) => {
      const filter = args[0];
      let output = '';

      if (filter === '--tech') {
        output = `
=== TECHNICAL SKILLS ===

💻 Programming Languages:
${skillCategories.languages.map(lang => `  • ${lang}`).join('\n')}

🤖 AI & Machine Learning:
${skillCategories.aiMachineLearning.map(skill => `  • ${skill}`).join('\n')}

☁️ Cloud & Databases:
${skillCategories.cloudDatabases.map(tech => `  • ${tech}`).join('\n')}

🛠️ Development Tools:
${skillCategories.developmentTools.map(tool => `  • ${tool}`).join('\n')}

🎮 Game Engines:
${skillCategories.gameEngines.map(engine => `  • ${engine}`).join('\n')}
        `.trim();
      } else {
        output = `
=== SKILLS OVERVIEW ===

💻 Programming Languages:
${skillCategories.languages.map(lang => `  • ${lang}`).join('\n')}

🤖 AI & Machine Learning:
${skillCategories.aiMachineLearning.map(skill => `  • ${skill}`).join('\n')}

☁️ Cloud & Databases:
${skillCategories.cloudDatabases.map(tech => `  • ${tech}`).join('\n')}

🛠️ Development Tools:
${skillCategories.developmentTools.map(tool => `  • ${tool}`).join('\n')}

⚡ Automation:
${skillCategories.automation.map(tool => `  • ${tool}`).join('\n')}

📊 Productivity:
${skillCategories.productivity.map(tool => `  • ${tool}`).join('\n')}

🎮 Game Engines:
${skillCategories.gameEngines.map(engine => `  • ${engine}`).join('\n')}

Type 'skills --tech' to see only technical skills.
        `.trim();
      }

      return { output, type: 'info' };
    }
  },
  {
    name: 'projects',
    aliases: ['p', 'portfolio'],
    description: 'Portfolio showcase with interactive media',
    handler: async (args) => {
      const filter = args[0];
      let filteredProjects = projects;

      if (filter === '--type') {
        const type = args[1];
        if (type) {
          filteredProjects = projects.filter(p => p.category === type);
        }
      }

      const output = `
=== PROJECT PORTFOLIO ===

${filteredProjects.map((project, index) => 
  `[${index + 1}] ${project.id.padEnd(25)} - ${project.title}`
).join('\n')}

Total Projects: ${filteredProjects.length}

Type 'view <project_id>' to explore details and media.
Type 'projects --type <category>' to filter by:
  • game-development
  • ai-automation  
  • web-app
  • research
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'view',
    aliases: ['v', 'show'],
    description: 'View project details and media',
    handler: async (args) => {
      const projectId = args[0];
      const project = projects.find(p => p.id === projectId);

      if (!project) {
        return {
          output: `❌ Project '${projectId}' not found. Type 'projects' to see available projects.`,
          type: 'error'
        };
      }

      const output = `
=== ${project.title.toUpperCase()} ===

📝 Description: ${project.description}

🛠️ Tech Stack: ${project.techStack.join(', ')}

📁 Category: ${project.category.replace('-', ' ').toUpperCase()}

🎬 Media: [${project.mediaType.toUpperCase()}] Available for viewing

${project.achievements ? `🏆 Achievements:\n${project.achievements.map(a => `  • ${a}`).join('\n')}` : ''}

[Media URL: ${project.mediaUrl}]
[Type 'back' to return to projects list]
      `.trim();

      return {
        output,
        type: 'media',
        mediaUrl: project.mediaUrl,
        interactive: true
      };
    }
  },
  {
    name: 'experience',
    aliases: ['exp', 'work'],
    description: 'Work history and achievements',
    handler: async () => {
      const output = `
=== WORK EXPERIENCE ===

${workExperience.map(exp => `
🏢 ${exp.company}
📋 Position: ${exp.position}
⏰ Duration: ${exp.duration}

📝 Description:
${exp.description.map(d => `  • ${d}`).join('\n')}

🏆 Achievements:
${exp.achievements.map(a => `  • ${a}`).join('\n')}

🛠️ Technologies: ${exp.technologies.join(', ')}
`).join('\n')}
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'education',
    aliases: ['edu', 'academic'],
    description: 'Academic background and certifications',
    handler: async () => {
      const output = `
=== EDUCATION & CERTIFICATIONS ===

${education.map(edu => `
🎓 ${edu.degree}
🏫 Institution: ${edu.institution}
⏰ Duration: ${edu.duration}
📊 CGPA: ${edu.cgpa}

📚 Description:
${edu.description.map(d => `  • ${d}`).join('\n')}
`).join('\n')}

🏆 Additional Certifications:
• Google Data Analytics Professional Certificate
• Various technical and professional certifications

Type 'certifications' to see all detailed certifications.
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'certifications',
    aliases: ['certs', 'cert'],
    description: 'View all certifications and training',
    handler: async () => {
      const output = `
=== CERTIFICATIONS & TRAINING ===

${certifications.map(cert => `
🏆 ${cert.title}
📚 Issuer: ${cert.issuer}
📅 Completed: ${cert.date}

📝 Description: ${cert.description}
`).join('\n')}

Total Certifications: ${certifications.length}
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'research',
    aliases: ['pub', 'publications'],
    description: 'View research publications',
    handler: async () => {
      const output = `
=== RESEARCH & PUBLICATIONS ===

${researchPublications.map(pub => `
📚 ${pub.title}
📖 ${pub.subtitle}
📰 Published in: ${pub.publishedIn}

📝 Summary: ${pub.summary}

💡 Highlights: ${pub.highlights}
`).join('\n')}

Total Publications: ${researchPublications.length}
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'timeline',
    aliases: ['tl', 'career'],
    description: 'Career progression visualization',
    handler: async () => {
      const output = `
=== CAREER TIMELINE ===

🕒 Interactive Timeline View Available

The timeline command provides a visual representation of my career 
progression, including:

📚 Education Journey:
• MSc Computer Science (2023-2025) - CGPA: 9.78
• BSc Computer Science (2020-2023) - CGPA: 9.38
• Class XII (2018-2020) - 75.85%
• Class X (2016-2018) - 82.33%

💼 Professional Experience:
• Operations and Technology Assistant at Rudyculous Entertainment (August 2025 - Present)
• Intern at Genesys International Corporation Ltd (January 2025 - February 2025)
• Intern at Genesys International Corporation Ltd (May 2024 - June 2024)

🎯 Key Milestones:
• Graduated with distinction from MSc CS
• Achieved high academic performance throughout
• Successfully transitioned to professional role
• Developed expertise in game development and AI
• Published research at ICETDT 2025 conference

Type 'timeline --visual' for enhanced timeline view.
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'achievements',
    aliases: ['ach', 'awards'],
    description: 'Notable accomplishments and publications',
    handler: async () => {
      const output = `
=== ACHIEVEMENTS & ACCOMPLISHMENTS ===

🌟 Notable Accomplishments:
• A: Secured 2nd Rank (Silver Medallist) in MSc Computer Science
• B: Appointed Head of the Discipline Committee (10th Grade)
• C: Performed the lead role of APJ Abdul Kalam in a 9th-grade theater production
• D: Ranked 1st in 7th Grade

🏆 Academic Excellence:
• MSc Computer Science: 9.78 CGPA (Distinction)
• BSc Computer Science: 9.38 CGPA (First Class)
• Consistent top academic performance

🎮 Game Development Projects:
• Successfully completed 6 Unreal Engine 5 projects
• Developed advanced simulation systems
• Created immersive gaming experiences
• Implemented complex AI behaviors

🤖 AI & Automation:
• Developed text summarization NLP system
• Created OpenBot - Advanced AI chat application with 50+ LLMs
• Implemented automation workflows
• Created intelligent systems for operations

💻 Technical Skills:
• Mastered multiple programming languages
• Expertise in cloud platforms and databases
• Proficient in development tools and automation
• Strong foundation in software engineering

📚 Certifications:
• 8+ Professional certifications from Google, Microsoft, LinkedIn, and Coursera
• Google Data Analytics Professional Certificate
• Various technical and professional certifications

🚀 Professional Growth:
• Successfully transitioned to OTA role
• Contributing to game development industry
• Building automation solutions for efficiency

Type 'projects' to see detailed project achievements.
Type 'experience' for work-related accomplishments.
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'contact',
    aliases: ['c', 'get-in-touch'],
    description: 'Professional contact information',
    handler: async () => ({
      output: `
=== CONTACT INFORMATION ===

📧 Email: ${personalInfo.email}
📱 Phone: ${personalInfo.phone}
🌐 LinkedIn: ${personalInfo.linkedin}
🔗 Gravatar: ${personalInfo.gravatar}
📱 Telegram: ${personalInfo.telegram}
📍 Location: ${personalInfo.location}

💼 Professional Inquiries:
Feel free to reach out for:
• Game Development Opportunities
• AI & Automation Projects
• Technical Consulting
• Collaboration Opportunities

💡 Best Ways to Connect:
• Schedule a meeting: Type 'schedule' to book through my gravatar
• Quick message: Type 'message' to contact me on Telegram
• Visit my website: ${personalInfo.gravatar}

I'm always open to discussing new projects and opportunities!
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'gravatar',
    aliases: ['qr', 'profile'],
    description: 'Interactive QR code and profile link',
    handler: async () => ({
      output: `
=== GRAVATAR PROFILE ===

🔗 Profile Website: ${personalInfo.gravatar}

📱 QR Code Information:
The QR code above contains a direct link to my professional profile website.

💼 What You'll Find:
• Professional portfolio and projects
• Contact information and scheduling
• Detailed work experience
• Skills and certifications
• Research publications
• Game development showcase

🎯 Direct Access:
• Visit: ${personalInfo.gravatar}
• Scan the QR code with your mobile device
• Book meetings and consultations
• View detailed project information

💡 Pro Tip: Use the 'schedule' command to book meetings or 'message' for quick Telegram contact.
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'schedule',
    aliases: ['meet', 'booking'],
    description: 'Schedule a meeting through gravatar',
    handler: async () => {
      const output = `
=== SCHEDULE A MEETING ===

📅 Meeting Scheduling:
Visit my gravatar website to schedule a meeting:
${personalInfo.gravatar}

💼 Available Meeting Types:
• Technical Discussion
• Project Collaboration
• Career Opportunities
• Game Development Consultation
• AI & Automation Consulting

⏰ Meeting Options:
• Video Call (Zoom/Google Meet)
• In-person (Mumbai area)
• Phone Call
• Email Discussion

🔗 Direct Link: ${personalInfo.gravatar}
[Click the link above to schedule your meeting]

💡 Note: All scheduling is handled through my gravatar website for better organization.
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'message',
    aliases: ['telegram', 'tg', 'msg'],
    description: 'Contact me on Telegram',
    handler: async () => {
      const output = `
=== TELEGRAM CONTACT ===

📱 Telegram Contact:
Username: ${personalInfo.telegram}

💬 Quick Communication:
Send me a message on Telegram for:
• Quick questions
• Project discussions
• Collaboration opportunities
• Technical queries
• Meeting coordination

🚀 Why Telegram:
• Instant messaging
• File sharing
• Voice messages
• Quick responses
• Professional communication

📱 Open Telegram and search: ${personalInfo.telegram}
[Start a conversation for quick responses]

💡 Note: For formal meetings and scheduling, please use my gravatar website: ${personalInfo.gravatar}
      `.trim();

      return { output, type: 'info' };
    }
  },
  {
    name: 'clear',
    aliases: ['cls', 'reset'],
    description: 'Clear terminal screen',
    handler: async () => ({
      output: '',
      type: 'clear'
    })
  },
  {
    name: 'theme',
    aliases: ['color', 'colors'],
    description: 'Change color themes (green/amber/blue/red/yellow/purple)',
    handler: async (args) => {
      const theme = args[0];
      const validThemes = ['green', 'amber', 'blue', 'red', 'yellow', 'purple'];
      
      if (!theme || !validThemes.includes(theme)) {
        return {
          output: `Available themes: ${validThemes.join(', ')}\nUsage: theme <theme_name>\n\nExample: theme red`,
          type: 'info'
        };
      }

      // Return a special command result that will trigger theme change
      return {
        output: `Theme changed to ${theme}. The change will be applied immediately.`,
        type: 'theme_change',
        theme: theme
      };
    }
  },
  {
    name: 'secret',
    aliases: ['easter-egg', 'hidden'],
    description: 'Hidden Easter egg content',
    hidden: true,
    handler: async () => ({
      output: `
🎮 Access granted! Welcome to the secret vault...

Hidden achievements unlocked:
[🏆] Command Explorer - Used 10+ different commands
[🔍] Project Deep Diver - Viewed 5+ project details  
[🎯] Secret Hunter - Found the hidden command
[📚] Knowledge Seeker - Read all education entries

Personal Fun Facts:
- Coffee consumption: ~4 cups/day while coding
- Favorite game genre: Tactical FPS & Simulation
- Dream project: Large-scale city simulation game
- Pet peeve: Poorly optimized algorithms

🎉 Congratulations! You've discovered the secret area!
      `.trim(),
      type: 'success'
    })
  },
  {
    name: 'status',
    aliases: ['sys', 'system'],
    description: 'System information and easter eggs',
    handler: async () => ({
      output: `
=== SYSTEM STATUS ===

🖥️ Terminal Portfolio OS v2.0
✅ All systems operational
🎯 Commands executed: [Dynamic counter]
🔍 Easter eggs found: [Dynamic counter]
🎮 Secret areas unlocked: [Dynamic counter]

💡 Pro Tips:
• Use Tab for command completion
• Arrow keys navigate command history
• Type 'secret' to unlock hidden content
• Try different themes with 'theme' command

🚀 Performance: Excellent
🎨 Current Theme: [Dynamic theme]
      `.trim(),
      type: 'info'
    })
  },
  {
    name: 'whoami',
    aliases: ['me', 'identity'],
    description: 'Extended personal bio with fun facts',
    handler: async () => ({
      output: `
=== WHO AM I ===

👤 Name: ${personalInfo.name}
🎯 Title: ${personalInfo.title}
🌍 Location: ${personalInfo.location}
🎂 Age: [Calculated from ${personalInfo.dob}]

🚀 My Story:
I'm a passionate technologist who believes in the power of 
innovation to solve real-world problems. My journey began with 
a fascination for how things work, which evolved into a love 
for creating digital experiences that matter.

🎮 Gaming Philosophy:
I see games as more than entertainment - they're platforms 
for storytelling, learning, and pushing technological boundaries. 
Every project I work on is an opportunity to explore new 
possibilities and create something unique.

🤖 AI & Automation Vision:
The future belongs to those who can harness the power of 
automation intelligently. I'm committed to building systems 
that enhance human capabilities rather than replace them.

💭 Personal Mantra:
"Code with purpose, build with passion, innovate with integrity."

Type 'about' for professional details.
Type 'secret' for hidden content.
      `.trim(),
      type: 'info'
    })
  }
];

export const findCommand = (input: string): Command | undefined => {
  const commandName = input.split(' ')[0].toLowerCase();
  console.log('Looking for command:', commandName);
  console.log('Available commands:', commands.map(cmd => cmd.name));
  
  const foundCommand = commands.find(cmd => 
    cmd.name === commandName || 
    (cmd.aliases && cmd.aliases.includes(commandName))
  );
  
  console.log('Found command:', foundCommand);
  return foundCommand;
};

export const getCommandSuggestions = (input: string): string[] => {
  const partial = input.toLowerCase();
  return commands
    .filter(cmd => !cmd.hidden && cmd.name.startsWith(partial))
    .map(cmd => cmd.name)
    .slice(0, 5);
};
